import { useEffect, useRef, type KeyboardEvent } from "react";
import ChannelConnection from "../../js/connection/ChannelConnection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane, faCommentDots, faTimes } from "@fortawesome/free-solid-svg-icons";
import { ChatMessage } from "../../js/connection/ChatMessage";

type Props = {
    connection: ChannelConnection | undefined;
    messages: ChatMessage[];
    onClose?: () => void;
};

export default function ChatBox({ connection, messages, onClose }: Props) {
    const chatBox = useRef<HTMLDivElement>(null);
    const input = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (chatBox.current) {
            chatBox.current.scrollTop = chatBox.current.scrollHeight;
        }
    }, [messages]);

    function onSendClicked() {
        const currentMessage = input.current?.value?.trim();
        if (currentMessage) {
            connection?.messager.sendChatMessage(currentMessage)?.then(() => {
                if (input.current) input.current.value = "";
            });
        }
    }

    function onInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
        if (event.key === "Enter") {
            event.preventDefault();
            onSendClicked();
        }
    }

    return (
        <div className="chatbox-root">
            <div className="chatbox-header">
                <div className="chatbox-header-left">
                    <FontAwesomeIcon icon={faCommentDots} className="chatbox-header-icon" />
                    <span className="chatbox-header-title">In-call messages</span>
                </div>
                {onClose && (
                    <button className="chatbox-close-btn" onClick={onClose} title="Close chat">
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                )}
            </div>

            <div className="chatbox-messages" ref={chatBox}>
                {messages.length === 0 && (
                    <div className="chatbox-empty">
                        <FontAwesomeIcon icon={faCommentDots} className="chatbox-empty-icon" />
                        <p>Messages are visible to everyone in this call</p>
                    </div>
                )}
                {messages.map((msg) => (
                    <div key={msg.id} className={`chat-msg ${msg.isSelf ? 'self' : 'other'}`}>
                        {!msg.isSelf && (
                            <div className="chat-avatar">{msg.user.charAt(0).toUpperCase()}</div>
                        )}
                        <div className="chat-msg-body">
                            {!msg.isSelf && (
                                <span className="chat-sender">{msg.user}</span>
                            )}
                            <div className="chat-bubble">{msg.message}</div>
                            <span className="chat-time">{msg.time}</span>
                        </div>
                    </div>
                ))}
            </div>

            <div className="chatbox-input-area">
                <input
                    ref={input}
                    className="chatbox-input"
                    type="text"
                    placeholder="Send a message to everyone"
                    maxLength={500}
                    onKeyDown={onInputKeyDown}
                />
                <button className="chatbox-send-btn" onClick={onSendClicked} title="Send message">
                    <FontAwesomeIcon icon={faPaperPlane} />
                </button>
            </div>

            <style>{`
                .chatbox-root {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: var(--hx-card);
                    color: var(--hx-ink-2);
                    font-family: inherit;
                }

                .chatbox-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 1rem 1.15rem;
                    border-bottom: 1px solid var(--hx-line);
                    flex-shrink: 0;
                    background: var(--hx-card);
                }

                .chatbox-header-left {
                    display: flex;
                    align-items: center;
                    gap: 0.6rem;
                }

                .chatbox-header-icon {
                    color: var(--hx-blue);
                    font-size: 0.85rem;
                }

                .chatbox-header-title {
                    font-size: 0.9rem;
                    font-weight: 650;
                    color: var(--hx-ink);
                    letter-spacing: -0.01em;
                }

                .chatbox-close-btn {
                    background: transparent;
                    border: none;
                    color: var(--hx-muted);
                    cursor: pointer;
                    width: 2rem;
                    height: 2rem;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.85rem;
                    transition: all 0.15s;
                }

                .chatbox-close-btn:hover {
                    background: var(--hx-line-2);
                    color: var(--hx-ink);
                }

                .chatbox-messages {
                    flex: 1;
                    overflow-y: auto;
                    padding: 1rem;
                    display: flex;
                    flex-direction: column;
                    gap: 0.85rem;
                }

                .chatbox-messages::-webkit-scrollbar {
                    display: block;
                    width: 4px;
                }

                .chatbox-messages::-webkit-scrollbar-track {
                    background: transparent;
                }

                .chatbox-messages::-webkit-scrollbar-thumb {
                    background: var(--hx-line);
                    border-radius: 4px;
                }

                .chatbox-empty {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    padding: 2rem;
                    text-align: center;
                    color: var(--hx-muted);
                }

                .chatbox-empty-icon {
                    font-size: 1.75rem;
                    opacity: 0.55;
                    color: var(--hx-blue);
                }

                .chatbox-empty p {
                    font-size: 0.84rem;
                    line-height: 1.5;
                    margin: 0;
                    max-width: 220px;
                }

                .chat-msg {
                    display: flex;
                    gap: 0.6rem;
                    align-items: flex-end;
                }

                .chat-msg.self {
                    flex-direction: row-reverse;
                }

                .chat-avatar {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, var(--hx-blue), var(--hx-blue-hover));
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.72rem;
                    font-weight: 700;
                    flex-shrink: 0;
                }

                .chat-msg-body {
                    display: flex;
                    flex-direction: column;
                    gap: 0.2rem;
                    max-width: 78%;
                }

                .chat-msg.self .chat-msg-body {
                    align-items: flex-end;
                }

                .chat-sender {
                    font-size: 0.7rem;
                    font-weight: 650;
                    color: var(--hx-muted);
                    padding: 0 0.25rem;
                }

                .chat-bubble {
                    padding: 0.55rem 0.8rem;
                    border-radius: 14px;
                    font-size: 0.875rem;
                    line-height: 1.45;
                    word-break: break-word;
                    background: var(--hx-line-2);
                    color: var(--hx-ink-2);
                    border: 1px solid var(--hx-line);
                }

                .chat-msg.self .chat-bubble {
                    background: var(--hx-blue-soft);
                    color: var(--hx-ink);
                    border: 1px solid var(--hx-blue-border);
                    border-bottom-right-radius: 4px;
                }

                .chat-msg.other .chat-bubble {
                    border-bottom-left-radius: 4px;
                }

                .chat-time {
                    font-size: 0.66rem;
                    color: var(--hx-muted-2);
                    padding: 0 0.25rem;
                }

                .chatbox-input-area {
                    display: flex;
                    gap: 0.5rem;
                    padding: 0.9rem 1rem 1rem;
                    border-top: 1px solid var(--hx-line);
                    flex-shrink: 0;
                    align-items: center;
                    background: var(--hx-card);
                }

                .chatbox-input {
                    flex: 1;
                    background: var(--hx-line-2);
                    border: 1px solid var(--hx-line);
                    border-radius: 999px;
                    padding: 0.65rem 1rem;
                    font-size: 0.875rem;
                    color: var(--hx-ink);
                    outline: none;
                    transition: border-color 0.15s, background 0.15s;
                    font-family: inherit;
                }

                .chatbox-input::placeholder {
                    color: var(--hx-muted-2);
                }

                .chatbox-input:focus {
                    border-color: var(--hx-blue-border);
                    background: var(--hx-card);
                }

                .chatbox-send-btn {
                    width: 38px;
                    height: 38px;
                    border-radius: 50%;
                    border: none;
                    background: linear-gradient(180deg, var(--hx-blue) 0%, var(--hx-blue-hover) 100%);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    flex-shrink: 0;
                    font-size: 0.85rem;
                    transition: filter 0.15s, transform 0.1s;
                    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.28);
                }

                .chatbox-send-btn:hover {
                    filter: brightness(1.08);
                    transform: translateY(-1px);
                }

                .chatbox-send-btn:active {
                    transform: translateY(0);
                }
            `}</style>
        </div>
    );
}
